import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Modal, Button, Input, HelpTooltip } from '@/kit';
import { Label } from '../../../components/ui/label';
import { applicationApi } from '../../../api/endpoints/application';
import { handleApiError } from '../../../api/errorMapping';
import { ApiError } from '../../../api/errors';
import { generateIdempotencyKey } from '../../../lib/idempotency';
import { Banner } from '../../../components/ui/Banner';
import { LevelsStep, levelsToPayload, type LevelDraft } from './steps/LevelsStep';
import { PermissionCatalogStep } from './steps/PermissionCatalogStep';
import {
    SeedRolesStep,
    DEFAULT_SUPER_ADMIN_SEED_ROLE,
    hasAdminRole,
    type AvailablePermission,
} from './steps/SeedRolesStep';
import {
    DefaultsStep,
    isAutoregisterCoherent,
    unknownAutoregisterRoleIds,
    hasUniversalMfaMethod,
} from './steps/DefaultsStep';
import {
    buildCreateBody,
    customPermissions,
    needsRoleReconciliation,
    permissionToUpsertBody,
    type SubmitPhase,
} from './wizardOrchestration';
import type {
    ApplicationDefaultsDraft,
    ApplicationPermissionDraft,
    SeedRoleWire,
} from '../../../api/types/ApplicationLocal';

// applicationId: slug kebab que re-valida el backend (igual que T4 / CreateApplicationForm). NO se
// autocalcula del nombre — el operador lo escribe (ej. "network").
const APPLICATION_ID_REGEX = /^[a-z0-9-]+$/;

// Defaults sanos del paso "Defaults": un draft vacío hace que el DefaultsStep caiga a sus propios
// defaults (MFA on, autoregistro off, etc.). Empezar con {} mantiene una sola fuente de defaults.
const EMPTY_DEFAULTS: ApplicationDefaultsDraft = {};

interface WizardStep {
    key: 'basic' | 'levels' | 'catalog' | 'roles' | 'defaults';
    title: string;
}

const STEPS: WizardStep[] = [
    { key: 'basic', title: 'Datos básicos' },
    { key: 'levels', title: 'Niveles' },
    { key: 'catalog', title: 'Catálogo de permisos' },
    { key: 'roles', title: 'Roles a sembrar' },
    { key: 'defaults', title: 'Defaults' },
];

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

/**
 * Wizard "Crear aplicación" (#3-a, Task D). Reemplaza el modal MVP T4 (`CreateApplicationForm`).
 * Orquesta los 4 componentes de paso controlados (LevelsStep / PermissionCatalogStep / SeedRolesStep /
 * DefaultsStep) en modo CREAR (buffer en memoria) + un paso "básico" propio (applicationId + nombre),
 * con navegación prev/next y validación por paso. El catálogo CUSTOM opera sobre un buffer porque los
 * endpoints de permisos requieren que la app YA exista — de ahí la orquestación multi-call del submit.
 *
 * Cableado EN VIVO entre pasos (una sola fuente de verdad por dato):
 *  - `availablePermissions` del SeedRolesStep = del catálogo (SYSTEM + CUSTOM del buffer).
 *  - `levels` + `availableSeedRoles` del DefaultsStep = de los pasos previos.
 *
 * Gateo del submit (helpers de los steps): `hasAdminRole(seedRoles)` (sino la app no es onboard-able) +
 * `isAutoregisterCoherent(defaults.autoregister, seedRoles)`. El botón "Crear" se deshabilita y se
 * explica el porqué.
 *
 * Orquestación del submit (DEC-016 — fallo parcial = borrador editable):
 *  1. `POST /applications` (seedRoles podados de refs CUSTOM — el catálogo CUSTOM aún no existe).
 *  2. Por cada permiso CUSTOM del buffer → `POST /applications/{id}/permissions`.
 *  3. Si algún seedRole referenciaba CUSTOM → `PUT /applications/{id}` reconciliando los seedRoles
 *     completos (ahora que el catálogo CUSTOM existe).
 *  4. Si (2)/(3) fallan tras crear la app, NO se borra: se avisa y se ofrece seguir la edición en el
 *     detalle. La app queda como plantilla parcial válida.
 *
 * Modal grande del kit (no ruta dedicada): el T4 ya abría desde la tabla con Modal y el wizard es
 * efímero; la edición (Task E) sí será la ruta `/platform/applications/{id}`.
 */
export function CreateApplicationWizard({ isOpen, onClose }: Props) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [stepIndex, setStepIndex] = useState(0);
    const [applicationId, setApplicationId] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [levels, setLevels] = useState<LevelDraft[]>([]);
    const [catalog, setCatalog] = useState<ApplicationPermissionDraft[]>([]);
    const [seedRoles, setSeedRoles] = useState<SeedRoleWire[]>([DEFAULT_SUPER_ADMIN_SEED_ROLE]);
    const [defaults, setDefaults] = useState<ApplicationDefaultsDraft>(EMPTY_DEFAULTS);

    const [phase, setPhase] = useState<SubmitPhase>('idle');
    // Error de una fase POSTERIOR a crear la app (la app ya existe = borrador). Distinto de un error de
    // la fase 1 (no se creó nada). Guarda el id para ofrecer "seguir en el detalle".
    const [partialError, setPartialError] = useState<{ applicationId: string; msg: string } | null>(
        null,
    );
    const [basicErrors, setBasicErrors] = useState<{
        applicationId?: string;
        displayName?: string;
    }>({});

    const step = STEPS[stepIndex];
    const levelsPayload = useMemo(() => levelsToPayload(levels), [levels]);

    // Cableado EN VIVO: el picker de permisos del SeedRolesStep ofrece los del catálogo (SYSTEM + CUSTOM).
    const availablePermissions: AvailablePermission[] = useMemo(
        () =>
            catalog.map((p) => ({
                permissionKey: p.permissionKey,
                displayName: p.displayName,
                description: p.description,
                category: p.category,
            })),
        [catalog],
    );

    // ── Gateo del submit (helpers de los steps) ───────────────────────────────────────────────────
    const adminOk = hasAdminRole(seedRoles);
    const autoregisterOk = isAutoregisterCoherent(defaults.autoregister, seedRoles);
    const unknownRoles = unknownAutoregisterRoleIds(defaults.autoregister, seedRoles);
    // DEC-RBAC-087: si el 2FA está prendido, la policy debe tener un método universal (TOTP o el OTP del
    // identificador principal). No se puede crear la app con el único OTP siendo el del secundario.
    const mfaOk = hasUniversalMfaMethod(
        defaults.securityPolicy,
        defaults.principalIdentifier ?? 'EMAIL',
    );
    const idValid = applicationId.trim() !== '' && APPLICATION_ID_REGEX.test(applicationId.trim());
    const nameValid = displayName.trim() !== '';
    const submitting = phase !== 'idle' && phase !== 'error' && phase !== 'done';
    const canSubmit = idValid && nameValid && adminOk && autoregisterOk && mfaOk && !submitting;

    const reset = (): void => {
        setStepIndex(0);
        setApplicationId('');
        setDisplayName('');
        setLevels([]);
        setCatalog([]);
        setSeedRoles([DEFAULT_SUPER_ADMIN_SEED_ROLE]);
        setDefaults(EMPTY_DEFAULTS);
        setPhase('idle');
        setPartialError(null);
        setBasicErrors({});
    };

    const close = (): void => {
        reset();
        onClose();
    };

    // Validación del paso "básico" antes de avanzar (los demás pasos son válidos por construcción).
    const validateBasic = (): boolean => {
        const errs: { applicationId?: string; displayName?: string } = {};
        if (!applicationId.trim()) errs.applicationId = 'Pon un ID para la aplicación.';
        else if (!APPLICATION_ID_REGEX.test(applicationId.trim()))
            errs.applicationId = 'Solo minúsculas, números y guiones. Ej: network';
        if (!displayName.trim()) errs.displayName = 'Pon un nombre para la aplicación.';
        setBasicErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const goNext = (): void => {
        if (step.key === 'basic' && !validateBasic()) return;
        setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
    };
    const goPrev = (): void => {
        setStepIndex((i) => Math.max(i - 1, 0));
    };

    const invalidateList = async (): Promise<void> => {
        await queryClient.invalidateQueries({ queryKey: ['applications'] });
    };

    // ── Orquestación multi-call del submit (DEC-016) ──────────────────────────────────────────────
    const submit = async (): Promise<void> => {
        if (!canSubmit) return;
        const id = applicationId.trim();
        setPartialError(null);

        // Fase 1: POST /applications (seedRoles podados de refs CUSTOM).
        setPhase('creating-app');
        try {
            await applicationApi.create(
                buildCreateBody({
                    applicationId: id,
                    displayName: displayName.trim(),
                    levels: levelsPayload,
                    seedRoles,
                    catalog,
                    defaults,
                }),
                { idempotencyKey: generateIdempotencyKey() },
            );
        } catch (err) {
            // Falló la creación: NO hay app, no hay borrador. Error inline si es colisión de id.
            setPhase('error');
            if (
                err instanceof ApiError &&
                (err.code === 'APPLICATION_ALREADY_EXISTS' || err.httpStatus === 409)
            ) {
                setStepIndex(0);
                setBasicErrors({
                    applicationId: `Ya existe una aplicación con el ID "${id}". Usa otro.`,
                });
                return;
            }
            handleApiError(err);
            return;
        }

        // La app ya existe: a partir de acá, cualquier fallo deja un BORRADOR editable (DEC-016).
        await invalidateList();
        const customs = customPermissions(catalog);

        // Fase 2: POST por cada permiso CUSTOM del buffer.
        if (customs.length > 0) {
            setPhase('creating-permissions');
            for (const perm of customs) {
                try {
                    await applicationApi.createPermission(id, permissionToUpsertBody(perm), {
                        idempotencyKey: generateIdempotencyKey(),
                    });
                } catch (err) {
                    setPhase('error');
                    setPartialError({
                        applicationId: id,
                        msg: `La aplicación "${displayName.trim()}" se creó, pero falló al guardar el permiso "${
                            perm.displayName || perm.permissionKey
                        }". Quedó como borrador: completala desde el detalle.`,
                    });
                    if (err instanceof ApiError) handleApiError(err);
                    return;
                }
            }
        }

        // Fase 3: si algún seedRole referenciaba CUSTOM, reconciliar con el catálogo ya creado.
        if (needsRoleReconciliation(seedRoles, catalog)) {
            setPhase('reconciling-roles');
            try {
                await applicationApi.update(id, {
                    displayName: displayName.trim(),
                    levels: levelsPayload,
                    seedRoles,
                    defaults,
                });
            } catch (err) {
                setPhase('error');
                setPartialError({
                    applicationId: id,
                    msg: `La aplicación "${displayName.trim()}" se creó con sus permisos, pero falló al asignar los permisos propios a los roles. Quedó como borrador: completala desde el detalle.`,
                });
                if (err instanceof ApiError) handleApiError(err);
                return;
            }
        }

        // Éxito total.
        setPhase('done');
        await invalidateList();
        toast.success('Aplicación creada');
        close();
    };

    const phaseLabel: Record<SubmitPhase, string> = {
        idle: '',
        'creating-app': 'Creando la aplicación…',
        'creating-permissions': 'Guardando los permisos propios…',
        'reconciling-roles': 'Asignando permisos a los roles…',
        done: '',
        error: '',
    };

    return (
        <Modal isOpen={isOpen} onClose={close} size="lg" title="Crear aplicación">
            <div className="mx-auto w-full max-w-2xl space-y-5 px-1 py-1">
                {/* Indicador de pasos (legible, no índices crudos). */}
                <ol className="flex flex-wrap items-center gap-2 text-xs">
                    {STEPS.map((s, i) => (
                        <li
                            key={s.key}
                            aria-current={i === stepIndex ? 'step' : undefined}
                            className={
                                i === stepIndex
                                    ? 'rounded-full bg-brand-accent px-3 py-1 font-medium text-brand-accent-foreground'
                                    : i < stepIndex
                                      ? 'rounded-full bg-muted px-3 py-1 text-foreground'
                                      : 'rounded-full px-3 py-1 text-muted-foreground'
                            }
                        >
                            {String(i + 1)}. {s.title}
                        </li>
                    ))}
                </ol>

                {/* Error de fallo parcial (la app YA se creó; quedó como borrador). TD-WEBAPP-027:
                    cerrado al shippear el detalle (Task E). El CTA navega al detalle con
                    partialError.applicationId — NO se re-dispara la Fase 1 (que daría 409 confuso). */}
                {partialError && (
                    <Banner variant="error" title="La aplicación ya se creó (quedó como borrador)">
                        <div className="space-y-3">
                            <p>{partialError.msg}</p>
                            <p className="text-sm">
                                No vuelvas a crearla: ya existe. Termina de configurarla en su
                                pantalla de detalle.
                            </p>
                            <Button
                                type="button"
                                size="sm"
                                rightIcon={<ArrowRight className="h-4 w-4" />}
                                onClick={() => {
                                    const id = partialError.applicationId;
                                    close();
                                    void navigate({
                                        to: '/platform/applications/$applicationId',
                                        params: { applicationId: id },
                                        search: { tab: 'catalog' },
                                    });
                                }}
                            >
                                Ir al detalle
                            </Button>
                        </div>
                    </Banner>
                )}

                {/* Cuerpo del paso actual. */}
                <div className="min-h-[18rem]">
                    {step.key === 'basic' && (
                        <div className="space-y-4">
                            <div className="space-y-1">
                                <div className="flex items-center gap-1">
                                    <Label htmlFor="caw-id">ID de la aplicación</Label>
                                    <HelpTooltip content="Identificador único de la aplicación (slug). Solo minúsculas, números y guiones. No editable luego." />
                                </div>
                                <Input
                                    id="caw-id"
                                    hint="Id único, ej. network. Solo minúsculas, números y guiones."
                                    value={applicationId}
                                    error={basicErrors.applicationId}
                                    onChange={(e) => {
                                        setApplicationId(e.target.value);
                                        setBasicErrors((p) => ({ ...p, applicationId: undefined }));
                                    }}
                                />
                            </div>
                            <Input
                                id="caw-name"
                                label="Nombre"
                                hint="Como quieres que se vea. Ej: Red de Agentes"
                                value={displayName}
                                error={basicErrors.displayName}
                                onChange={(e) => {
                                    setDisplayName(e.target.value);
                                    setBasicErrors((p) => ({ ...p, displayName: undefined }));
                                }}
                            />
                        </div>
                    )}

                    {step.key === 'levels' && <LevelsStep value={levels} onChange={setLevels} />}

                    {step.key === 'catalog' && (
                        <PermissionCatalogStep
                            value={catalog}
                            onChange={setCatalog}
                            levels={levelsPayload}
                        />
                    )}

                    {step.key === 'roles' && (
                        <SeedRolesStep
                            value={seedRoles}
                            onChange={setSeedRoles}
                            availablePermissions={availablePermissions}
                        />
                    )}

                    {step.key === 'defaults' && (
                        <DefaultsStep
                            value={defaults}
                            onChange={setDefaults}
                            levels={levelsPayload}
                            availableSeedRoles={seedRoles}
                        />
                    )}
                </div>

                {/* Gateo del submit: explicar por qué está deshabilitado (solo en el último paso). */}
                {stepIndex === STEPS.length - 1 && (!adminOk || !autoregisterOk) && (
                    <div className="space-y-2">
                        {!adminOk && (
                            <Banner variant="warning">
                                Para poder crear la aplicación necesitas al menos un rol con
                                permisos de admin (gestión de roles/usuarios). Revisa el paso “Roles
                                a sembrar”.
                            </Banner>
                        )}
                        {!autoregisterOk && (
                            <Banner variant="warning">
                                El autoregistro referencia {String(unknownRoles.length)} rol(es) que
                                ya no existen entre los roles a sembrar. Corregilo en el paso
                                “Defaults”.
                            </Banner>
                        )}
                    </div>
                )}

                {/* Navegación. */}
                <div className="flex items-center justify-between border-t pt-4">
                    <div className="text-xs text-muted-foreground" aria-live="polite">
                        {submitting && phaseLabel[phase]}
                    </div>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            leftIcon={<ChevronLeft className="h-4 w-4" />}
                            disabled={stepIndex === 0 || submitting}
                            onClick={goPrev}
                        >
                            Atrás
                        </Button>
                        {stepIndex < STEPS.length - 1 ? (
                            <Button
                                type="button"
                                rightIcon={<ChevronRight className="h-4 w-4" />}
                                onClick={goNext}
                            >
                                Siguiente
                            </Button>
                        ) : partialError ? (
                            // Fallo parcial: la app YA existe. NO ofrecer "Crear aplicación" (re-dispararía
                            // la Fase 1 → 409). La única salida es el CTA "Ir al detalle" del banner; acá
                            // solo dejamos cerrar el wizard. (TD-WEBAPP-027.)
                            <Button type="button" variant="outline" onClick={close}>
                                Cerrar
                            </Button>
                        ) : (
                            <Button
                                type="button"
                                isLoading={submitting}
                                disabled={!canSubmit}
                                onClick={() => {
                                    void submit();
                                }}
                            >
                                Crear aplicación
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
}
